import type { ValidationConfig } from '../validationEngine/types';
import { transformValidationConfig } from '../validationEngine/transformValidationConfig';
import { addressValidationConfig } from './addressValidationConfig';
import { getRules, withoutRequiredRules } from './validationConfigRuleUtils';
import { isGapTermValid } from '../vehicleProtection/vehicleProtectionUtils';

export const vehicleProtectionProductValidationConfig: ValidationConfig = {
    productName: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'save',
                messages: 'Required.'
            }
        ]
    }
};

export const isValidNonZeroVppValue = (value: unknown): boolean => Number(value) > 0;

export type ContractApiErroredVppAddressField = {
    flag: 'hasCityError' | 'hasZipError' | 'hasStateError' | 'hasStreetAddressError';
    sourceKey: 'city' | 'postalCode' | 'stateProvince' | 'addressLine1';
    apiKey: 'apiCity' | 'apiZip' | 'apiState' | 'apiStreetAddress';
    hasApiErrorKey: 'hasApiCityError' | 'hasApiZipError' | 'hasApiStateError' | 'hasApiStreetAddressError';
};

export const CONTRACT_API_ERRORED_VPP_GAP_REQUIRED_ADDRESS_FIELDS: ContractApiErroredVppAddressField[] = [
    {
        flag: 'hasCityError',
        sourceKey: 'city',
        apiKey: 'apiCity',
        hasApiErrorKey: 'hasApiCityError'
    },
    {
        flag: 'hasStreetAddressError',
        sourceKey: 'addressLine1',
        apiKey: 'apiStreetAddress',
        hasApiErrorKey: 'hasApiStreetAddressError'
    }
];

export const CONTRACT_API_ERRORED_VPP_ADDRESS_FIELDS: ContractApiErroredVppAddressField[] = [
    ...CONTRACT_API_ERRORED_VPP_GAP_REQUIRED_ADDRESS_FIELDS,
    {
        flag: 'hasZipError',
        sourceKey: 'postalCode',
        apiKey: 'apiZip',
        hasApiErrorKey: 'hasApiZipError'
    },
    {
        flag: 'hasStateError',
        sourceKey: 'stateProvince',
        apiKey: 'apiState',
        hasApiErrorKey: 'hasApiStateError'
    }
];

export const contractApiErroredVppValidationConfig: ValidationConfig = {
    apiMiles: {
        rules: [
            {
                type: 'CUSTOM',
                flow: 'contract',
                params: { evaluator: isValidNonZeroVppValue },
                messages: 'Miles is required'
            }
        ]
    },
    apiTerms: {
        rules: [
            {
                type: 'CUSTOM',
                flow: 'contract',
                params: { evaluator: isValidNonZeroVppValue },
                messages: 'Term is required'
            }
        ]
    },
    apiProviderName: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'Provider name is required'
            }
        ]
    },
    apiCity: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'City is required'
            }
        ]
    },
    apiZip: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'ZIP is required'
            }
        ]
    },
    apiState: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'State is required'
            }
        ]
    },
    apiStreetAddress: {
        rules: [
            {
                type: 'REQUIRED',
                flow: 'contract',
                messages: 'Address is required'
            }
        ]
    }
};

// GAP Provider (After Market) address validations.
// This config is consumed:
// - In `contractFieldConfig` under the `gap` group for GC alerts.
// - In the ProviderAddressDetails component via `useFormValidationWithContractFlow`.
const baseProviderValidationConfig: ValidationConfig = {
    // Reuse shared address rules; label customization is handled via `ctx.fieldLabels`.
    streetAddress: (addressValidationConfig as any).streetAddress,
    city: (addressValidationConfig as any).city,
    state: (addressValidationConfig as any).state,
    zip: {
        rules: withoutRequiredRules(getRules((addressValidationConfig as any).zip))
    },
    terms: {
        rules: [
            {
                type: 'CUSTOM',
                flow: 'contract',
                params: {
                    evaluator: (value: number, ctx: any) => {
                        const dealTerm = ctx?.values?.terms;
                        if (!dealTerm || !value) return true;
                        return isGapTermValid(value, dealTerm);
                    }
                },
                messages: 'GAP term cannot exceed the deal term. Please adjust GAP term to be equal to or less than the deal term.'
            }
        ]
    }
};

export const providerValidationConfig: ValidationConfig = transformValidationConfig(baseProviderValidationConfig, {
    fieldLabels: {
        streetAddress: 'Address'
    }
});
